import React, { useState, useEffect, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { loanMappingService } from 'api/services/loanMappingService';
import { glReplacementService } from 'api/services/glReplacementService';
import type { PendingReplacementCheck } from 'api/types/glReplacement.types';
import { ReturnedHistorySection } from 'components/common/ReturnedHistorySection';
import { ModalLoadingSkeleton } from 'components/common';
import { approvalService } from 'api/services/approvalService';
import { SyutSelect } from 'components/ui';
import { useCustomers, useCreateCustomer } from 'hooks/api';
import { CustomerFormModal } from 'components/modals/modules/customer-module/CustomerRegistryModal';
import type { CustomerFormData } from 'components/modals/modules/customer-module/CustomerRegistryModal';

// ============================================================================
// Types
// ============================================================================

/** GL from a loan mapping (used in outgoing checkbox table) */
export interface OutgoingGLFormData {
  mappedGlId: string;
  glNumber: string;
  customerName: string;
  netWeight: number;
  allocationRatio: number;
  selected: boolean;
  outstandingPrincipal: string;
  outstandingInterest: string;
  tokenLossCharge: string;
}

/** Incoming GL entry (dynamic list) */
export interface IncomingGLFormData {
  incomingGlNumber: string;
  incomingCustomerId: string;
  incomingNetWeight: string;
  incomingOriginalLoanAmount: string;
  incomingLoanDate: string;
  documentationCharge: string;
}

/** Form data for creating/editing a GL replacement */
export interface GlReplacementFormData {
  mappingId: string;
  mappingRef: string;
  customerName: string;
  customerId: string;
  loanAccountNumber: string;
  addonOutstandingPrincipal: string;
  addonOutstandingInterest: string;
  availableGls: OutgoingGLFormData[];
  incomingEntries: IncomingGLFormData[];
}

/** GL Replacement record for the view modal */
export interface GlReplacementRecord {
  id: string;
  replacementRef?: string;
  mappingId: string;
  mappingRef?: string;
  customerName: string;
  customerId: string;
  loanAccountNumber?: string;
  addonOutstandingPrincipal: number;
  addonOutstandingInterest: number;
  totalOutgoingWeight: number;
  totalIncomingWeight: number;
  weightDifference: number;
  totalWeightBefore: number;
  settlementRatio: number;
  addonSettlementPrincipal: number;
  addonSettlementInterest: number;
  totalOutgoingGlSettlement: number;
  totalIncomingDisbursement: number;
  netCashFlow: number;
  outgoingEntries?: {
    id: string;
    outgoingMappedGlId: string;
    outgoingGlNumber?: string;
    outgoingCustomerName?: string;
    outgoingNetWeight: number;
    outgoingAllocationRatio: number;
    outgoingGlOutstandingPrincipal: number;
    outgoingGlOutstandingInterest: number;
    tokenLossCharge: number;
    glSettlement: number;
  }[];
  incomingEntries?: {
    id: string;
    incomingGlNumber: string;
    incomingCustomerId?: string;
    incomingCustomerName?: string;
    incomingNetWeight: number;
    incomingOriginalLoanAmount: number;
    incomingLoanDate?: string;
    documentationCharge: number;
    disbursementAmount: number;
  }[];
  statusCode?: string;
  statusName?: string;
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: string;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  rowVersion?: number;
  businessUnitName?: string;
}

// ============================================================================
// Section Header Style
// ============================================================================

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 600,
  marginBottom: '0.75rem',
  color: 'var(--primary)',
  borderBottom: '2px solid var(--primary)',
  paddingBottom: '0.5rem',
};

const thStyle: React.CSSProperties = {
  padding: '0.5rem',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: '0.75rem',
  borderBottom: '2px solid var(--border-color)',
};

const thRight: React.CSSProperties = { ...thStyle, textAlign: 'right' };

// ============================================================================
// Form Modal
// ============================================================================

interface GlReplacementFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: GlReplacementFormData;
  onFormDataChange: (data: GlReplacementFormData) => void;
  onSave: () => void;
  onSubmit?: () => void;
  onClose: () => void;
  isProcessing?: boolean;
}

export const GlReplacementFormModal: React.FC<GlReplacementFormModalProps> = ({
  isOpen,
  mode,
  formData,
  onFormDataChange,
  onSave,
  onSubmit,
  onClose,
  isProcessing = false,
}) => {
  const [searchAccountNo, setSearchAccountNo] = useState('');
  const [searchGLAccountNo, setSearchGLAccountNo] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [pendingCheck, setPendingCheck] = useState<PendingReplacementCheck | null>(null);

  // ---- Customer selection ----
  const { data: customers, refetch: refetchCustomers } = useCustomers();
  const { createCustomer } = useCreateCustomer();
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [customerModalTargetIdx, setCustomerModalTargetIdx] = useState(0);
  const [customerFormData, setCustomerFormData] = useState<CustomerFormData>({ customerId: '', customerName: '' });

  const customerOptions = useMemo(() =>
    customers.map(c => ({ value: c.customerId, label: `${c.customerId} - ${c.customerName}` })),
    [customers]
  );

  const handleOpenCustomerModal = (idx: number) => {
    setCustomerModalTargetIdx(idx);
    setCustomerFormData({ customerId: '', customerName: '' });
    setCustomerModalOpen(true);
  };

  const handleSaveNewCustomer = async () => {
    const { customerId, customerName } = customerFormData;
    if (!customerId.trim() || !customerName.trim()) return;
    const cleanId = customerId.toUpperCase().replace(/\s/g, '');
    try {
      await createCustomer({ customerId: cleanId, customerName: customerName.trim() });
      await refetchCustomers();
      const updatedEntries = [...formData.incomingEntries];
      updatedEntries[customerModalTargetIdx] = {
        ...updatedEntries[customerModalTargetIdx],
        incomingCustomerId: cleanId,
      };
      onFormDataChange({ ...formData, incomingEntries: updatedEntries });
      setCustomerModalOpen(false);
    } catch (err: any) {
      const detail = err?.response?.data;
      alert(detail?.error?.message || detail?.message || (typeof detail === 'string' ? detail : null) || err?.message || 'Failed to create customer');
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSearchAccountNo('');
      setSearchGLAccountNo('');
      setSearchPerformed(mode === 'edit');
      setSearchError('');
      setValidated(false);
      setPendingCheck(null);
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const isSearchDone = mode === 'edit' || (searchPerformed && !!formData.mappingId);

  // ---- Derived: selected outgoing GLs ----
  const selectedOutgoingGls = formData.availableGls.filter((gl) => gl.selected);
  const incomingEntries = formData.incomingEntries;

  // ---- Calculations ----
  const totalOutgoingWeight = selectedOutgoingGls.reduce((s, gl) => s + gl.netWeight, 0);
  const totalIncomingWeight = incomingEntries.reduce((s, e) => s + (parseFloat(e.incomingNetWeight) || 0), 0);
  const weightDifference = totalOutgoingWeight - totalIncomingWeight;
  const totalWeightBefore = formData.availableGls.reduce((s, gl) => s + gl.netWeight, 0);

  const settlementRatio = weightDifference > 0 && totalWeightBefore > 0
    ? weightDifference / totalWeightBefore
    : 0;

  const addonPrincipal = parseFloat(formData.addonOutstandingPrincipal) || 0;
  const addonInterest = parseFloat(formData.addonOutstandingInterest) || 0;
  const addonSettlementPrincipal = Math.round(addonPrincipal * settlementRatio);
  const addonSettlementInterest = addonInterest; // Full amount (Part A rule)

  const totalOutgoingGLSettlement = selectedOutgoingGls.reduce(
    (s, gl) => s + (parseFloat(gl.outstandingPrincipal) || 0) + (parseFloat(gl.outstandingInterest) || 0) + (parseFloat(gl.tokenLossCharge) || 0), 0
  );
  const totalIncomingDisbursement = incomingEntries.reduce(
    (s, e) => s + ((parseFloat(e.incomingOriginalLoanAmount) || 0) - (parseFloat(e.documentationCharge) || 0)), 0
  );
  const netCashFlow = Math.round(totalIncomingDisbursement - (totalOutgoingGLSettlement + addonSettlementPrincipal + addonSettlementInterest));

  // ---- Validation ----
  const glsWithMissingAmounts = selectedOutgoingGls.filter(
    (gl) => !gl.outstandingPrincipal || isNaN(parseFloat(gl.outstandingInterest))
  );
  const incomingGlNumberErrors = incomingEntries.map((e) =>
    e.incomingGlNumber && e.incomingGlNumber.length !== 14 ? 'Must be exactly 14 characters' : ''
  );
  const hasAccountErrors = incomingGlNumberErrors.some(Boolean);
  const incompleteIncomingEntries = incomingEntries.filter(
    (e) => !e.incomingGlNumber || !e.incomingNetWeight
  );

  // ---- Search Handler ----
  const handleSearch = async () => {
    const accNo = searchAccountNo.trim().replace(/\s/g, '');
    const glAccNo = searchGLAccountNo.trim().replace(/\s/g, '');
    if (!accNo && !glAccNo) {
      setSearchError('Enter an Account Number or GL Account Number to search.');
      return;
    }
    setSearchLoading(true);
    setSearchError('');
    try {
      const result = await loanMappingService.searchByAccount(accNo || undefined, glAccNo || undefined);
      if (!result) {
        setSearchError('No matching loan mapping found. Please verify the account number.');
        setSearchPerformed(true);
        return;
      }
      setPendingCheck(null);
      try {
        const checkResult = await glReplacementService.checkPending(String(result.id));
        if (checkResult.data?.hasPending) {
          setPendingCheck(checkResult.data);
        }
      } catch { /* non-fatal */ }

      const openGls: OutgoingGLFormData[] = (result.mappedGls ?? [])
        .filter((gl) => gl.statusCode === 'OPEN')
        .map((gl) => ({
          mappedGlId: String(gl.id),
          glNumber: gl.glNumber ?? '',
          customerName: gl.glCustomerName ?? '',
          netWeight: gl.netWeight,
          allocationRatio: gl.allocationRatio,
          selected: false,
          outstandingPrincipal: '',
          outstandingInterest: '',
          tokenLossCharge: '',
        }));

      onFormDataChange({
        ...formData,
        mappingId: String(result.id),
        mappingRef: `MAP-${String(result.id).substring(0, 8).toUpperCase()}`,
        customerName: result.customerName ?? '',
        customerId: result.customerId ?? '',
        loanAccountNumber: result.addonLoanAccountNumber ?? '',
        addonOutstandingPrincipal: '',
        addonOutstandingInterest: '',
        availableGls: openGls,
        incomingEntries: [],
      });
      setSearchPerformed(true);
      setSearchError('');
    } catch {
      setSearchError('Failed to search. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleResetSearch = () => {
    setSearchAccountNo('');
    setSearchGLAccountNo('');
    setSearchPerformed(false);
    setSearchError('');
    setPendingCheck(null);
    onFormDataChange({
      ...formData,
      mappingId: '',
      mappingRef: '',
      customerName: '',
      customerId: '',
      loanAccountNumber: '',
      addonOutstandingPrincipal: '',
      addonOutstandingInterest: '',
      availableGls: [],
      incomingEntries: [],
    });
  };

  // ---- Outgoing GL Handlers (checkbox table) ----
  const handleGLToggle = (mappedGlId: string) => {
    onFormDataChange({
      ...formData,
      availableGls: formData.availableGls.map((gl) =>
        gl.mappedGlId === mappedGlId
          ? { ...gl, selected: !gl.selected, ...(!gl.selected ? {} : { outstandingPrincipal: '', outstandingInterest: '', tokenLossCharge: '' }) }
          : gl
      ),
    });
  };

  const handleGLFieldChange = (mappedGlId: string, field: 'outstandingPrincipal' | 'outstandingInterest' | 'tokenLossCharge', value: string) => {
    onFormDataChange({
      ...formData,
      availableGls: formData.availableGls.map((gl) =>
        gl.mappedGlId === mappedGlId ? { ...gl, [field]: value } : gl
      ),
    });
  };

  // ---- Incoming GL Handlers (dynamic list) ----
  const handleAddIncomingEntry = () => {
    const today = new Date().toISOString().split('T')[0]; // yyyy-MM-dd
    onFormDataChange({
      ...formData,
      incomingEntries: [
        ...incomingEntries,
        {
          incomingGlNumber: '',
          incomingCustomerId: '',
          incomingNetWeight: '',
          incomingOriginalLoanAmount: '',
          incomingLoanDate: today,
          documentationCharge: '',
        },
      ],
    });
  };

  const handleRemoveIncomingEntry = (idx: number) => {
    onFormDataChange({
      ...formData,
      incomingEntries: incomingEntries.filter((_, i) => i !== idx),
    });
  };

  const handleIncomingFieldChange = (idx: number, field: keyof IncomingGLFormData, value: string) => {
    onFormDataChange({
      ...formData,
      incomingEntries: incomingEntries.map((e, i) => (i === idx ? { ...e, [field]: value } : e)),
    });
  };

  return (
    <>
      <style>{`.gl-replacement-customer-select .syut-select__control { min-height: 32px !important; max-height: 32px !important; } .gl-replacement-customer-select .syut-select__value-container { padding: 0 8px !important; } .gl-replacement-customer-select .syut-select__indicators { height: 30px !important; } .gl-replacement-customer-select .syut-select__input-container { margin: 0 !important; padding: 0 !important; }`}</style>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'create' ? 'New GL Replacement' : 'Edit GL Replacement'}
          </h2>
          <button className="modal-close" onClick={onClose} disabled={isProcessing} style={isProcessing ? { opacity: 0.3, pointerEvents: 'none' as const } : undefined}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1, position: 'relative' }}>
          {/* Processing overlay */}
          {isProcessing && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(255,255,255,0.6)', zIndex: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 600 }}>
                <span style={{
                  display: 'inline-block', width: '20px', height: '20px',
                  border: '3px solid rgba(37,99,235,0.2)', borderTopColor: 'var(--primary)',
                  borderRadius: '50%', animation: 'glReplacementSpin 0.6s linear infinite',
                }} />
                Processing...
              </div>
              <style>{`@keyframes glReplacementSpin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* ========== SECTION A: Search / Loan Details ========== */}
          <h3 style={sectionHeaderStyle}>A. Loan Details</h3>

          {mode === 'create' && !isSearchDone && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                Search by Account Number or GL Account Number to find the loan mapping.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Account Number</label>
                  <input
                    className="form-input"
                    type="text"
                    value={searchAccountNo}
                    onChange={(e) => setSearchAccountNo(e.target.value.replace(/\s/g, ''))}
                    placeholder="e.g., ACC-2025-001"
                    style={{ border: '1px solid #334155' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">GL Account Number</label>
                  <input
                    className="form-input"
                    type="text"
                    value={searchGLAccountNo}
                    onChange={(e) => setSearchGLAccountNo(e.target.value.replace(/\s/g, ''))}
                    placeholder="e.g., GL-2000"
                    style={{ border: '1px solid #334155' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  style={{ height: '34px', display: 'flex', alignItems: 'center', gap: '0.375rem', whiteSpace: 'nowrap', ...(searchLoading ? { opacity: 0.7, cursor: 'wait' } : {}) }}
                >
                  <IconSystem.search size={16} />
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              {searchError && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  marginTop: '0.75rem', padding: '0.625rem 0.875rem',
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem',
                  color: '#dc2626', fontSize: '0.8rem',
                }}>
                  <IconSystem.alert size={14} />
                  {searchError}
                </div>
              )}
            </div>
          )}

          {isSearchDone && (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem',
                background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Mapping Reference</div>
                  <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{formData.mappingRef}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer</div>
                  <div style={{ fontWeight: 600 }}>{formData.customerName} ({formData.customerId})</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Account Number</div>
                  <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{formData.loanAccountNumber}</div>
                </div>
              </div>
              {mode === 'create' && (
                <button
                  type="button"
                  onClick={handleResetSearch}
                  style={{
                    marginTop: '0.5rem', background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--primary)', fontSize: '0.75rem', padding: '0.25rem 0',
                    textDecoration: 'underline',
                  }}
                >
                  ← Search Again
                </button>
              )}
            </div>
          )}

          {/* Pending Replacement Warning */}
          {isSearchDone && pendingCheck?.hasPending && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
              padding: '0.875rem 1rem', marginBottom: '1.25rem',
              background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.5rem',
              color: '#991b1b', fontSize: '0.8rem', lineHeight: 1.5,
            }}>
              <IconSystem.alert size={18} style={{ flexShrink: 0, marginTop: '1px', color: '#dc2626' }} />
              <div>
                <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Pending GL Replacement Exists</div>
                <div>{pendingCheck.message}</div>
              </div>
            </div>
          )}

          {/* ========== SECTION B: Add-On Loan Outstanding ========== */}
          {isSearchDone && !pendingCheck?.hasPending && (
            <>
              <h3 style={sectionHeaderStyle}>B. Add-On Loan Outstanding</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Outstanding Principal *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.addonOutstandingPrincipal}
                    onChange={(e) => onFormDataChange({ ...formData, addonOutstandingPrincipal: e.target.value })}
                    placeholder="0.00"
                    min={0}
                    step="0.01"
                    style={{ border: `1px solid ${validated && !formData.addonOutstandingPrincipal ? '#dc2626' : '#334155'}` }}
                  />
                  {validated && !formData.addonOutstandingPrincipal && (
                    <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.25rem', display: 'block' }}>Required</span>
                  )}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Outstanding Interest</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.addonOutstandingInterest}
                    onChange={(e) => onFormDataChange({ ...formData, addonOutstandingInterest: e.target.value })}
                    placeholder="0.00"
                    min={0}
                    step="0.01"
                    style={{ border: '1px solid #334155' }}
                  />
                </div>
              </div>
            </>
          )}

          {/* ========== SECTION C: Outgoing GL (Being Removed) ========== */}
          {isSearchDone && !pendingCheck?.hasPending && formData.availableGls.length > 0 && (
            <>
              <h3 style={sectionHeaderStyle}>C. Outgoing GL (Being Removed)</h3>

              {validated && glsWithMissingAmounts.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                  padding: '0.75rem 1rem', marginBottom: '1rem',
                  background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem',
                  color: '#92400e', fontSize: '0.8rem', fontWeight: 500,
                }}>
                  <IconSystem.warning size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span>
                    Outstanding Principal and Interest are required for selected Gold Loans:{' '}
                    <strong>{glsWithMissingAmounts.map((gl) => gl.glNumber).join(', ')}</strong>
                  </span>
                </div>
              )}

              <div style={{ overflowX: 'auto', marginBottom: '1.5rem', border: '1px solid var(--border-color)', borderRadius: '0.375rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)' }}>
                      <th style={{ ...thStyle, textAlign: 'center', width: '32px', borderRight: '1px solid var(--border-color)', padding: '0.375rem 0.25rem' }}></th>
                      <th style={{ ...thStyle, borderRight: '1px solid var(--border-color)', padding: '0.375rem' }}>GL Number</th>
                      <th style={{ ...thStyle, borderRight: '1px solid var(--border-color)', padding: '0.375rem' }}>Customer</th>
                      <th style={{ ...thRight, borderRight: '1px solid var(--border-color)', padding: '0.375rem', whiteSpace: 'nowrap' }}>Wt (g)</th>
                      <th style={{ ...thRight, borderRight: '1px solid var(--border-color)', padding: '0.375rem', whiteSpace: 'nowrap' }}>Alloc %</th>
                      <th style={{ ...thRight, borderRight: '1px solid var(--border-color)', padding: '0.375rem', whiteSpace: 'nowrap' }}>O/S Principal</th>
                      <th style={{ ...thRight, borderRight: '1px solid var(--border-color)', padding: '0.375rem', whiteSpace: 'nowrap' }}>O/S Interest</th>
                      <th style={{ ...thRight, borderRight: '1px solid var(--border-color)', padding: '0.375rem', whiteSpace: 'nowrap' }}>Token Loss</th>
                      <th style={{ ...thRight, padding: '0.375rem', whiteSpace: 'nowrap' }}>Settlement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.availableGls.map((gl, glIdx) => {
                      const isMissing = validated && gl.selected && (!gl.outstandingPrincipal || isNaN(parseFloat(gl.outstandingInterest)));
                      const isLast = glIdx === formData.availableGls.length - 1;
                      const tdBase: React.CSSProperties = { padding: '0.375rem', borderRight: '1px solid var(--border-color)', borderBottom: isLast ? 'none' : '1px solid var(--border-color)' };
                      const tdLast: React.CSSProperties = { padding: '0.375rem', borderBottom: isLast ? 'none' : '1px solid var(--border-color)' };
                      return (
                        <tr key={gl.mappedGlId} style={{
                          background: isMissing ? 'rgba(245, 158, 11, 0.06)' : gl.selected ? 'rgba(59, 130, 246, 0.06)' : 'transparent',
                        }}>
                          <td style={{ ...tdBase, textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={gl.selected}
                              onChange={() => handleGLToggle(gl.mappedGlId)}
                              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                          </td>
                          <td style={{ ...tdBase, fontFamily: 'monospace' }}>{gl.glNumber}</td>
                          <td style={{ ...tdBase, fontSize: '0.75rem' }}>{gl.customerName}</td>
                          <td style={{ ...tdBase, textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                          <td style={{ ...tdBase, textAlign: 'right', fontWeight: 500 }}>{(gl.allocationRatio * 100).toFixed(2)}%</td>
                          <td style={{ ...tdBase, textAlign: 'right' }}>
                            <input
                              className="form-input"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={gl.outstandingPrincipal}
                              onChange={(e) => handleGLFieldChange(gl.mappedGlId, 'outstandingPrincipal', e.target.value)}
                              disabled={!gl.selected}
                              style={{
                                width: '95px', textAlign: 'right', fontSize: '0.75rem', padding: '0.25rem 0.375rem',
                                borderColor: isMissing && !gl.outstandingPrincipal ? '#f59e0b' : undefined,
                              }}
                            />
                          </td>
                          <td style={{ ...tdBase, textAlign: 'right' }}>
                            <input
                              className="form-input"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={gl.outstandingInterest}
                              onChange={(e) => handleGLFieldChange(gl.mappedGlId, 'outstandingInterest', e.target.value)}
                              disabled={!gl.selected}
                              style={{
                                width: '95px', textAlign: 'right', fontSize: '0.75rem', padding: '0.25rem 0.375rem',
                                borderColor: isMissing && isNaN(parseFloat(gl.outstandingInterest)) ? '#f59e0b' : undefined,
                              }}
                            />
                          </td>
                          <td style={{ ...tdBase, textAlign: 'right' }}>
                            <input
                              className="form-input"
                              type="number"
                              min="0"
                              step="0.01"
                              placeholder="0.00"
                              value={gl.tokenLossCharge}
                              onChange={(e) => handleGLFieldChange(gl.mappedGlId, 'tokenLossCharge', e.target.value)}
                              disabled={!gl.selected}
                              style={{
                                width: '95px', textAlign: 'right', fontSize: '0.75rem', padding: '0.25rem 0.375rem',
                              }}
                            />
                          </td>
                          <td style={{ ...tdLast, textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
                            {gl.selected
                              ? formatCurrency((parseFloat(gl.outstandingPrincipal) || 0) + (parseFloat(gl.outstandingInterest) || 0) + (parseFloat(gl.tokenLossCharge) || 0))
                              : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ========== SECTION D: Incoming GL (New Replacement) ========== */}
          {isSearchDone && !pendingCheck?.hasPending && (
            <>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <h3 style={{ ...sectionHeaderStyle, flex: 1, marginBottom: incomingEntries.length === 0 ? '0.75rem' : sectionHeaderStyle.marginBottom }}>D. Incoming GL (New Replacement)</h3>
                {incomingEntries.length === 0 && (
                  <button
                    type="button"
                    onClick={handleAddIncomingEntry}
                    title="Add incoming GL entry"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#16a34a', display: 'flex', alignItems: 'center', padding: '0.15rem',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <IconSystem.add size={14} />
                  </button>
                )}
              </div>

              {validated && incompleteIncomingEntries.length > 0 && incomingEntries.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                  padding: '0.75rem 1rem', marginBottom: '1rem',
                  background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem',
                  color: '#92400e', fontSize: '0.8rem', fontWeight: 500,
                }}>
                  <IconSystem.warning size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span>Each incoming entry requires a GL Number and Eligible Weight.</span>
                </div>
              )}

              {incomingEntries.length === 0 && (
                <div style={{
                  padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)',
                  fontSize: '0.8rem', border: '1px dashed var(--border-color)', borderRadius: '0.5rem',
                  marginBottom: '0.75rem',
                }}>
                  No incoming GL entries added yet. Click the + button to add a new replacement GL.
                </div>
              )}

              {incomingEntries.map((entry, idx) => (
                <div key={idx} style={{
                  marginBottom: '0.75rem', padding: '0.75rem 1rem', borderRadius: '0.375rem',
                  border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>
                      #{idx + 1}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveIncomingEntry(idx)}
                      title="Remove entry"
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        color: '#dc2626', display: 'flex', alignItems: 'center', padding: '0.15rem',
                      }}
                    >
                      <IconSystem.trash size={14} />
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'start' }}>
                    <div className="form-group" style={{ margin: 0, flex: '1 1 120px', minWidth: '120px' }}>
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>GL Number *</label>
                      <input
                        type="text"
                        className="form-input"
                        value={entry.incomingGlNumber}
                        onChange={(e) => handleIncomingFieldChange(idx, 'incomingGlNumber', e.target.value.replace(/\s/g, ''))}
                        placeholder="14-char GL #"
                        maxLength={14}
                        style={{ border: `1px solid ${(validated && !entry.incomingGlNumber) || incomingGlNumberErrors[idx] ? 'var(--danger, #dc2626)' : '#334155'}`, fontSize: '0.8rem', height: '32px', padding: '0.375rem 0.5rem' }}
                      />
                      {incomingGlNumberErrors[idx] && (
                        <div style={{ fontSize: '0.65rem', color: 'var(--danger, #dc2626)', marginTop: '0.1rem', whiteSpace: 'nowrap' }}>
                          {incomingGlNumberErrors[idx]}
                        </div>
                      )}
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: '2 1 160px', minWidth: '160px' }}>
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Customer ID</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                        <div style={{ flex: 1 }} className="gl-replacement-customer-select">
                          <SyutSelect
                            options={customerOptions}
                            value={entry.incomingCustomerId}
                            onChange={(val) => handleIncomingFieldChange(idx, 'incomingCustomerId', (val as string) || '')}
                            placeholder="Select..."
                            isSearchable
                            isClearable
                            size="sm"
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleOpenCustomerModal(idx)}
                          title="Add New Customer"
                          style={{
                            background: 'transparent', color: 'var(--text-secondary)',
                            border: '1px solid #334155', borderRadius: '0.25rem',
                            width: '28px', height: '28px', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-tertiary)'; e.currentTarget.style.color = 'var(--primary)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = '#334155'; }}
                        >
                          <IconSystem.add size={12} />
                        </button>
                      </div>
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: '0 1 70px', minWidth: '70px' }}>
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Weight (g) *</label>
                      <input
                        type="number"
                        className="form-input"
                        value={entry.incomingNetWeight}
                        onChange={(e) => handleIncomingFieldChange(idx, 'incomingNetWeight', e.target.value)}
                        placeholder="0.000"
                        min={0}
                        step="0.001"
                        style={{ border: `1px solid ${validated && !entry.incomingNetWeight ? '#dc2626' : '#334155'}`, fontSize: '0.8rem', height: '32px', padding: '0.375rem 0.5rem' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: '1 1 90px', minWidth: '90px' }}>
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Loan Amount</label>
                      <input
                        type="number"
                        className="form-input"
                        value={entry.incomingOriginalLoanAmount}
                        onChange={(e) => handleIncomingFieldChange(idx, 'incomingOriginalLoanAmount', e.target.value)}
                        placeholder="0.00"
                        min={0}
                        step="0.01"
                        style={{ border: '1px solid #334155', fontSize: '0.8rem', height: '32px', padding: '0.375rem 0.5rem' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: '1 1 110px', minWidth: '110px' }}>
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Loan Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={entry.incomingLoanDate}
                        onChange={(e) => handleIncomingFieldChange(idx, 'incomingLoanDate', e.target.value)}
                        style={{ border: '1px solid #334155', fontSize: '0.8rem', height: '32px', padding: '0.375rem 0.5rem' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: '1 1 120px', minWidth: '120px' }}>
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Doc. Charge</label>
                      <input
                        type="number"
                        className="form-input"
                        value={entry.documentationCharge}
                        onChange={(e) => handleIncomingFieldChange(idx, 'documentationCharge', e.target.value)}
                        placeholder="0.00"
                        min={0}
                        step="0.01"
                        style={{ border: '1px solid #334155', fontSize: '0.8rem', height: '32px', padding: '0.375rem 0.5rem' }}
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0, flex: '2 1 140px', minWidth: '140px' }}>
                      <label className="form-label" style={{ fontSize: '0.68rem' }}>Disbursement Amount</label>
                      <div style={{
                        fontSize: '0.85rem', fontWeight: 600, height: '32px',
                        display: 'flex', alignItems: 'center', color: 'var(--text-primary)',
                      }}>
                        {formatCurrency((parseFloat(entry.incomingOriginalLoanAmount) || 0) - (parseFloat(entry.documentationCharge) || 0))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Add button — right-aligned, only when entries exist (otherwise shown in title row) */}
              {incomingEntries.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem', paddingRight: '1rem' }}>
                  <button
                    type="button"
                    onClick={handleAddIncomingEntry}
                    title="Add incoming GL entry"
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#16a34a', display: 'flex', alignItems: 'center', padding: '0.15rem',
                    }}
                  >
                    <IconSystem.add size={14} />
                  </button>
                </div>
              )}
            </>
          )}

          {/* ========== SECTION E: Settlement & Cash Flow Summary ========== */}
          {isSearchDone && !pendingCheck?.hasPending && (selectedOutgoingGls.length > 0 || incomingEntries.length > 0) && (
            <>
              <h3 style={sectionHeaderStyle}>E. Settlement & Cash Flow Summary</h3>

              {/* Weight analysis */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '1rem', marginBottom: '1rem',
                background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Outgoing Weight</div>
                  <div style={{ fontWeight: 600 }}>{totalOutgoingWeight.toFixed(3)}g</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Incoming Weight</div>
                  <div style={{ fontWeight: 600 }}>{totalIncomingWeight.toFixed(3)}g</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Weight Difference</div>
                  <div style={{ fontWeight: 600, color: weightDifference > 0 ? '#dc2626' : '#16a34a' }}>
                    {weightDifference.toFixed(3)}g
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Weight Before</div>
                  <div style={{ fontWeight: 600 }}>{totalWeightBefore.toFixed(3)}g</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Ratio</div>
                  <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{(settlementRatio * 100).toFixed(4)}%</div>
                  {weightDifference <= 0 && (
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>No reduction</div>
                  )}
                </div>
              </div>

              {/* Settlement breakdown */}
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '1rem', marginBottom: '1rem',
                background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Sett. Principal</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(addonSettlementPrincipal)}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                    {formatCurrency(addonPrincipal)} x {(settlementRatio * 100).toFixed(4)}%
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Sett. Interest</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(addonSettlementInterest)}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Full Amount</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outgoing GL Settlement</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(totalOutgoingGLSettlement)}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                    Σ (Principal + Interest + Token Loss)
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Incoming GL Disbursement</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(totalIncomingDisbursement)}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                    Σ (Loan Amount − Doc. Charge)
                  </div>
                </div>
              </div>

              {/* Net cash flow */}
              <div style={{
                background: 'var(--bg-tertiary)', padding: '1rem', borderRadius: '0.5rem',
                border: '1px solid var(--border-color)', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Net Cash Flow</div>
                <div style={{ fontWeight: 700, fontSize: '1.1rem', color: netCashFlow >= 0 ? '#16a34a' : '#dc2626' }}>
                  {formatCurrency(Math.round(netCashFlow))}
                </div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>
                  Incoming Disbursement − (Outgoing GL Settlement + Add-On Settlement)
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {hasAccountErrors && (
              <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>
                GL numbers must be exactly 14 characters
              </span>
            )}
            {!hasAccountErrors && validated && selectedOutgoingGls.length === 0 && (
              <span style={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>
                Select at least one outgoing GL
              </span>
            )}
            {!hasAccountErrors && validated && incomingEntries.length === 0 && selectedOutgoingGls.length > 0 && (
              <span style={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>
                Add at least one incoming GL entry
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={isProcessing}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={() => { setValidated(true); onSave(); }}
              disabled={!!pendingCheck?.hasPending || hasAccountErrors || isProcessing}
              style={pendingCheck?.hasPending || hasAccountErrors || isProcessing ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              <IconSystem.save size={16} />
              {mode === 'create' ? 'Create as Draft' : 'Save as Draft'}
            </button>
            {onSubmit && (
              <button
                className="btn btn-primary"
                onClick={() => { setValidated(true); onSubmit(); }}
                disabled={!!pendingCheck?.hasPending || hasAccountErrors || isProcessing}
                style={{
                  background: '#16a34a', borderColor: '#16a34a',
                  ...(pendingCheck?.hasPending || hasAccountErrors || isProcessing ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                }}
              >
                <IconSystem.check size={16} />
                Submit
              </button>
            )}
          </div>
        </div>
      </div>

      <CustomerFormModal
        isOpen={customerModalOpen}
        mode="create"
        formData={customerFormData}
        onFormDataChange={setCustomerFormData}
        onSave={handleSaveNewCustomer}
        onClose={() => setCustomerModalOpen(false)}
      />
    </>
  );
};

// ============================================================================
// View Modal
// ============================================================================

interface GlReplacementViewModalProps {
  isOpen: boolean;
  record: GlReplacementRecord | null;
  onClose: () => void;
}

export const GlReplacementViewModal: React.FC<GlReplacementViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  const [outgoingEntries, setOutgoingEntries] = useState<GlReplacementRecord['outgoingEntries']>([]);
  const [incomingEntries, setIncomingEntries] = useState<GlReplacementRecord['incomingEntries']>([]);
  const [glLoading, setGlLoading] = useState(false);
  const [detailCreatedByName, setDetailCreatedByName] = useState<string | undefined>();
  const [approvalInfo, setApprovalInfo] = useState<{
    approvedBy?: string; approvedAt?: string;
    rejectedBy?: string; rejectedAt?: string; rejectedRemark?: string | null;
  }>({});

  useEffect(() => {
    if (isOpen && record?.id) {
      setGlLoading(true);
      setOutgoingEntries([]);
      setIncomingEntries([]);
      setDetailCreatedByName(undefined);
      setApprovalInfo({});
      glReplacementService.getById(record.id)
        .then((response) => {
          const detail = response.data;
          if (detail) {
            setDetailCreatedByName(detail.createdByName);
            setOutgoingEntries(detail.outgoingEntries as any ?? []);
            setIncomingEntries(detail.incomingEntries as any ?? []);
          }
        })
        .catch(() => {})
        .finally(() => setGlLoading(false));
      approvalService.getHistory('gl_replacement', record.id)
        .then((res) => {
          const history = res.data ?? [];
          const approved = history.find((h: any) => h.action?.toLowerCase() === 'approved');
          const rejected = history.find((h: any) => h.action?.toLowerCase() === 'rejected');
          setApprovalInfo({
            approvedBy: approved?.actionedByName, approvedAt: approved?.actionedAt,
            rejectedBy: rejected?.actionedByName, rejectedAt: rejected?.actionedAt, rejectedRemark: rejected?.remark ?? undefined,
          });
        })
        .catch(() => {});
    } else {
      setOutgoingEntries([]);
      setIncomingEntries([]);
      setDetailCreatedByName(undefined);
      setApprovalInfo({});
    }
  }, [isOpen, record?.id]);

  if (!isOpen || !record) return null;

  const outList = outgoingEntries ?? [];
  const inList = incomingEntries ?? [];

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            GL Replacement {record.replacementRef}
            <span style={getStatusBadgeStyle(record.statusCode ?? '')}>{(record.statusCode ?? '').replace(/_/g, ' ')}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Section 1: Request Details */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Request Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Mapping Reference</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.mappingRef}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Account Number</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.loanAccountNumber}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer ID</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.customerId}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer Name</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.customerName}</div>
              </div>
            </div>
          </div>

          {/* Section 2: Weight & Settlement Summary */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Weight & Settlement Summary
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outgoing Weight</div>
                <div style={{ fontWeight: 600 }}>{(record.totalOutgoingWeight ?? 0).toFixed(3)}g</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Incoming Weight</div>
                <div style={{ fontWeight: 600 }}>{(record.totalIncomingWeight ?? 0).toFixed(3)}g</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Weight Difference</div>
                <div style={{ fontWeight: 600, color: (record.weightDifference ?? 0) > 0 ? '#dc2626' : '#16a34a' }}>
                  {(record.weightDifference ?? 0).toFixed(3)}g
                </div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Wt. Before</div>
                <div style={{ fontWeight: 600 }}>{(record.totalWeightBefore ?? 0).toFixed(3)}g</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Ratio</div>
                <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{((record.settlementRatio ?? 0) * 100).toFixed(4)}%</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Sett. Principal</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementPrincipal)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Sett. Interest</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementInterest)}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Full Amount</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outstanding P / I</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonOutstandingPrincipal)} / {formatCurrency(record.addonOutstandingInterest)}</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Outgoing GL Settlement</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.totalOutgoingGlSettlement ?? 0)}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>P + I + Token Loss Charge</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Incoming Disbursement</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.totalIncomingDisbursement ?? 0)}</div>
                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Loan Amount − Doc. Charge</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Net Cash Flow</div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: (record.netCashFlow ?? 0) >= 0 ? '#16a34a' : '#dc2626' }}>
                  {formatCurrency(Math.round(record.netCashFlow ?? 0))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Outgoing GL Entries */}
          <h3 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
            Outgoing GL (Being Removed)
          </h3>
          {glLoading ? (
            <ModalLoadingSkeleton sections={1} showTable tableCols={6} tableRows={2} />
          ) : (
            <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>GL Number</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thRight}>Eligible Weight (g)</th>
                    <th style={thRight}>Alloc. Ratio</th>
                    <th style={thRight}>Outstanding P</th>
                    <th style={thRight}>Outstanding I</th>
                    <th style={thRight}>Token Loss Charge</th>
                    <th style={thRight}>GL Settlement</th>
                  </tr>
                </thead>
                <tbody>
                  {outList.map((e, idx) => (
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{e.outgoingGlNumber}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.75rem' }}>{e.outgoingCustomerName || '-'}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(e.outgoingNetWeight ?? 0).toFixed(3)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{((e.outgoingAllocationRatio ?? 0) * 100).toFixed(2)}%</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(e.outgoingGlOutstandingPrincipal ?? 0)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(e.outgoingGlOutstandingInterest ?? 0)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(e.tokenLossCharge ?? 0)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(e.glSettlement ?? 0)}</td>
                    </tr>
                  ))}
                  {outList.length === 0 && (
                    <tr><td colSpan={9} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>No outgoing entries</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Section 4: Incoming GL Entries */}
          <h3 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
            Incoming GL (New Replacement)
          </h3>
          {glLoading ? (
            <ModalLoadingSkeleton sections={1} showTable tableCols={5} tableRows={2} />
          ) : (
            <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-tertiary)' }}>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>GL Number</th>
                    <th style={thStyle}>Customer</th>
                    <th style={thRight}>Eligible Weight (g)</th>
                    <th style={thRight}>Original Loan Amt</th>
                    <th style={thRight}>Doc. Charge</th>
                    <th style={thRight}>Disbursement Amt</th>
                    <th style={thStyle}>Loan Date</th>
                  </tr>
                </thead>
                <tbody>
                  {inList.map((e, idx) => (
                    <tr key={e.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                      <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{e.incomingGlNumber}</td>
                      <td style={{ padding: '0.5rem', fontSize: '0.75rem' }}>{e.incomingCustomerName ? `${e.incomingCustomerId} - ${e.incomingCustomerName}` : (e.incomingCustomerId || '-')}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(e.incomingNetWeight ?? 0).toFixed(3)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(e.incomingOriginalLoanAmount ?? 0)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(e.documentationCharge ?? 0)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(e.disbursementAmount ?? 0)}</td>
                      <td style={{ padding: '0.5rem' }}>{e.incomingLoanDate ? formatDate(e.incomingLoanDate) : '-'}</td>
                    </tr>
                  ))}
                  {inList.length === 0 && (
                    <tr><td colSpan={8} style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)' }}>No incoming entries</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Section 5: Submission & Approval Info */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Submission & Approval Info
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{detailCreatedByName || record.createdByName || record.createdBy || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created At</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{formatDate(record.createdAt)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.submittedByName || record.submittedBy || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted At</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.submittedAt ? formatDate(record.submittedAt) : '-'}</div>
              </div>
            </div>
            {approvalInfo.approvedBy && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Approved By</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#16a34a' }}>{approvalInfo.approvedBy}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Approved At</div>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{approvalInfo.approvedAt ? formatDate(approvalInfo.approvedAt) : '-'}</div>
                </div>
              </div>
            )}
            {approvalInfo.rejectedBy && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejected By</div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#dc2626' }}>{approvalInfo.rejectedBy}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejected At</div>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{approvalInfo.rejectedAt ? formatDate(approvalInfo.rejectedAt) : '-'}</div>
                  </div>
                </div>
                {approvalInfo.rejectedRemark && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejection Remark</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{approvalInfo.rejectedRemark}</div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Section 6: Returned History
              Shown for RETURNED status (active return cycle) AND for REJECTED records
              that came from a prior return, so the return trail stays visible after rejection. */}
          {(record.statusCode === 'RETURNED' || approvalInfo.rejectedBy) && (
            <ReturnedHistorySection recordType="gl_replacement" recordId={record.id} />
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};
